import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';

export default function AdminAnalytics() {
  const [loading, setLoading] = useState(true);
  const [ordersData, setOrdersData] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [dailyRevenue, setDailyRevenue] = useState([]);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    avgOrderValue: 0,
    pendingOrders: 0,
    deliveredOrders: 0,
    inTransitOrders: 0
  });
  const [dateRange, setDateRange] = useState('30'); // days

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      // Fetch all orders
      const res = await fetch('/api/admin/orders', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      let orders = await res.json();
      if (!Array.isArray(orders)) orders = orders.orders || [];

      // Filter by date range
      const days = parseInt(dateRange);
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      const filteredOrders = orders.filter(o => new Date(o.createdAt) >= cutoff);

      // Calculate stats
      const totalOrders = filteredOrders.length;
      const totalRevenue = filteredOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0);
      const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

      const pendingOrders = filteredOrders.filter(o => o.status === 'pending').length;
      const deliveredOrders = filteredOrders.filter(o => o.status === 'delivered').length;
      const inTransitOrders = filteredOrders.filter(o => o.status === 'in_transit' || o.status === 'claimed').length;

      setStats({
        totalOrders,
        totalRevenue,
        avgOrderValue,
        pendingOrders,
        deliveredOrders,
        inTransitOrders
      });

      // Status distribution for pie chart
      const statusCounts = {};
      filteredOrders.forEach(o => {
        statusCounts[o.status] = (statusCounts[o.status] || 0) + 1;
      });
      const statusChartData = Object.entries(statusCounts).map(([name, value]) => ({
        name: name === 'in_transit' ? 'In Transit' :
             name === 'claimed' ? 'Claimed' :
             name.charAt(0).toUpperCase() + name.slice(1),
        value
      }));
      setStatusData(statusChartData);

      // Daily orders and revenue for line chart
      const dailyMap = {};
      filteredOrders.forEach(o => {
        const date = new Date(o.createdAt).toLocaleDateString();
        if (!dailyMap[date]) {
          dailyMap[date] = { date, orders: 0, revenue: 0 };
        }
        dailyMap[date].orders++;
        dailyMap[date].revenue += o.totalAmount || 0;
      });
      const dailyChartData = Object.values(dailyMap).sort((a, b) =>
        new Date(a.date) - new Date(b.date)
      );
      setDailyRevenue(dailyChartData);
      setOrdersData(dailyChartData);

    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pending',
      claimed: 'Claimed',
      in_transit: 'In Transit',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
      refunded: 'Refunded',
      damaged: 'Damaged'
    };
    return labels[status] || status;
  };

  if (loading) return <div className="text-center py-8">Loading analytics...</div>;

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
        <div className="flex gap-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="border rounded px-3 py-2"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
          <Link to="/admin" className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">
            Back to Admin
          </Link>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Total Orders</div>
          <div className="text-2xl font-bold">{stats.totalOrders}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Total Revenue</div>
          <div className="text-2xl font-bold text-green-600">${stats.totalRevenue.toFixed(2)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Avg Order Value</div>
          <div className="text-2xl font-bold">${stats.avgOrderValue.toFixed(2)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Pending Orders</div>
          <div className="text-2xl font-bold text-yellow-600">{stats.pendingOrders}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">In Transit</div>
          <div className="text-2xl font-bold text-blue-600">{stats.inTransitOrders}</div>
        </div>
      </div>

      {/* Revenue Trend Chart */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Revenue Trend</h2>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={dailyRevenue}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="revenue" stroke="#16a34a" fill="#22c55e" fillOpacity={0.3} name="Revenue ($)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Daily Orders and Status Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Daily Orders Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Daily Orders</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={ordersData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="orders" fill="#3b82f6" name="Orders" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Order Status Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Order Status Distribution</h2>
          {statusData.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Delivery Performance Metrics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Delivery Performance</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {stats.deliveredOrders}
            </div>
            <div className="text-sm text-gray-600">Completed Deliveries</div>
          </div>
          <div className="p-4 bg-yellow-50 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">
              {stats.inTransitOrders}
            </div>
            <div className="text-sm text-gray-600">Active Deliveries</div>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {stats.totalOrders > 0 ? Math.round((stats.deliveredOrders / stats.totalOrders) * 100) : 0}%
            </div>
            <div className="text-sm text-gray-600">Completion Rate</div>
          </div>
        </div>
      </div>
    </div>
  );
}
